import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * One-tap add to cart for the Brand product row section
 * (sections/shugar-product-row.liquid).
 *
 * Mirrors product-form.js: dispatches the standard CartLinesUpdateEvent and
 * resolves it with the refreshed cart, so the cart icon bubble, the cart
 * drawer (including auto-open) and the header live region all update exactly
 * as they do for the theme's own add-to-cart buttons.
 */
class ShugarAddToCart extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = async (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target)?.closest('button');
    if (!(button instanceof HTMLButtonElement) || button.disabled || this.#busy) return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;

    this.#busy = true;
    this.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    // Ask Shopify to re-render the cart sections in the same request, the way
    // product-form.js does, so the drawer can morph without a second fetch.
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const root = window.Shopify?.routes?.root ?? '/';
    const addUrl = window.Theme?.routes?.cart_add_url ?? `${root}cart/add`;

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Add to cart failed');
      }

      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((res) =>
        res.json()
      );

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'shugar-product-row',
          sourceId: this.dataset.productId,
          itemCount: 1,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });

      this.#flash('is-added');
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.#flash('is-error');
    } finally {
      this.#busy = false;
      this.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  };

  /**
   * Briefly applies a state class to give feedback on the button.
   * @param {'is-added' | 'is-error'} state
   */
  #flash(state) {
    this.classList.add(state);
    setTimeout(() => this.classList.remove(state), 1200);
  }
}

if (!customElements.get('shugar-add-to-cart')) {
  customElements.define('shugar-add-to-cart', ShugarAddToCart);
}
